import { forwardRef, useEffect, useState } from 'react';
import PureModal from 'react-pure-modal';
import UIkit from 'uikit';
import type { ContentTypes } from './Types';
import { createValidator, useForm } from '../../utils/hooks/useForm';
import TextInputComponent from '../utils/textInput/TextInputComponent';
import TagInput from '../utils/tagInput/TagInput';
import type { ApiResource } from '../../api/services/config/resource/IResourceService';
import type {
  ApiBlobResource,
  ApiReturnResource,
  ApiReturnTag,
} from '@highimpact-frontend/api-types';
import TagService from '../../api/services/config/tags/TagService';
import FilePicker from '../utils/filePicker/FilePicker';
import resourceService from '../../api/services/config/resource/ResourceService';
import { includeByName } from '../../utils/functions';

// Props.
interface CreateRessourceProps {
  project_id: number;
  site_id?: number;
  device_id?: number;
  handleClose: (value: boolean) => void;
  isModalOpen: boolean;
}

const contentTypes: ContentTypes[] = ['text', 'image'];

type DefinedContentTypes = Exclude<ContentTypes, null | undefined>;

const contentTypesPretty: Partial<Record<DefinedContentTypes, string>> = {
  text: 'Texte',
  image: 'Image',
};

function CreateRessource({
  project_id,
  site_id,
  isModalOpen = false,
  handleClose = () => {},
}: CreateRessourceProps) {
  const [selectedTags, setSelectedTagsIds] = useState<ApiReturnTag[]>([]);
  const [projectTags, setProjectTags] = useState<ApiReturnTag[]>([]);
  const [globalTags, setGlobalTags] = useState<ApiReturnTag[]>([]);

  // Default form values.
  const defaultValues: ApiResource & { file?: File | null } = {
    project_id: project_id,
    site_id: site_id,
    name: '',
    type: 'text',
    content_type: '',
    resource_type: 'text',
    value: null,
    file: null,
  };

  // Validator for the form fields.
  const validator = createValidator<ApiResource & { file?: File | null }>({
    project_id: [],
    site_id: [],
    name: [(n) => (n.length <= 3 ? 'Entrer au moins 4 caractères' : undefined)],
    type: [],
    content_type: [],
    resource_type: [],
    value: [],
    file: [],
  });

  const closeModal = () => {
    handleClose(isModalOpen);
    methods.reset();
    return true;
  };

  const processAddTags = async (
    newTags: ApiReturnTag[],
    resource: ApiReturnResource
  ): Promise<void> => {
    await Promise.all(
      newTags.map(async (newTag) => {
        if (!includeByName(newTag, globalTags)) {
          // Attendre que le tag soit ajouté avant de l'associer au projet
          await TagService.add(newTag);
          await TagService.associateTagToProject(
            [newTag.name],
            resource!.project_id
          );
        }
        // Associer le tag à la ressource
        await TagService.associateTagToResource(resource!.id, [newTag.name]);
      })
    );
  };

  const onSubmit = async (data: ApiBlobResource | ApiResource) => {
    try {
      console.log(methods.watch('type'));

      const response: ApiReturnResource = await resourceService.add(
        data as ApiResource
      );
      await processAddTags(selectedTags, response);
      UIkit.notification({
        message: 'La ressource a été créée avec succès',
        status: 'success',
      });
      closeModal();
    } catch (error: any) {
      console.error('Error:', error);
      UIkit.notification({
        message:
          error.response?.data?.message ||
          'Une erreur est survenue lors de la création de la ressource.',
        status: 'danger',
      });
      closeModal();
    }
  };

  // Define form hook.
  const methods = useForm(defaultValues, validator, onSubmit);

  // Check for errors on a specific field
  const isErrorOnField = (field: keyof ApiResource): string => {
    return methods.getErrors(field).length > 0
      ? 'form-danger'
      : methods.hasSubmitted
      ? 'form-success'
      : '';
  };

  const handleTypeChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const value = e.target.value as ContentTypes;
    const ivalue = value === 'text' ? 'text' : 'blob';
    methods.setMultipleValues({ type: value, resource_type: ivalue });
  };

  const handleSelectedTags = (values: ApiReturnTag[]) => {
    setSelectedTagsIds(values);
  };

  useEffect(() => {
    TagService.getAll().then((response) => setGlobalTags(response));
    TagService.getAllFromProject(project_id).then((response) =>
      setProjectTags(response)
    );
  }, [project_id]);

  return (
    <PureModal
      header={`Créer une ressource`}
      className="pure-modal-management"
      isOpen={isModalOpen}
      closeButton={<span className="icon" uk-icon="close"></span>}
      closeButtonPosition="header"
      onClose={() => {
        handleClose(isModalOpen);
        methods.reset();
        return true;
      }}
    >
      <form onSubmit={methods.onSubmit} className="form ressource">
        <TextInputComponent
          label="Nom"
          name="name"
          placeholder="Nom de la ressource"
          onChange={(stringValue) => methods.setValue('name', stringValue)}
          required
          errors = {methods.hasSubmitted ? {tag: "Submited", "errors": methods.getErrors("name")}: {tag:"Not submitted"}}/>
        <div className="uk-margin">
          <label>Choisir un type</label>
          <select onChange={handleTypeChange} className="uk-select">
            {contentTypes.map((type) => (
              <option key={type} value={type!}>
                {contentTypesPretty[type!]}
              </option>
            ))}
          </select>
        </div>
        {methods.watch('type') === 'text' ? (
          <div className="uk-margin">
            <label htmlFor="form-horizontal-text">Ajouter du texte</label>
            <div className="uk-form-controls">
              <input
                className="uk-input"
                id="form-horizontal-text"
                type="text"
                placeholder="Ajouter du texte"
                onChange={(e) => methods.setValue('value', e.target.value)}
              />
            </div>
          </div>
        ) : (
          <FilePicker
            filehandler={(file: File | null) => methods.setValue('file', file)}
          />
        )}
        <TagInput
          predicate={(substring) => (data) =>
            Object.values(data).some((value) =>
              String(value).toLowerCase().includes(substring[0]!.toLowerCase())
            )}
          selectionValues={
            projectTags
              ? (projectTags as {
                  id: number;
                  name: string;
                  is_generic: boolean;
                }[])
              : []
          }
          handleSelectedValues={handleSelectedTags}
        />
        <button
          type="submit"
          className="uk-button uk-button-default"
          disabled={
            selectedTags.length === 0 ||
            (methods.watch('type') === 'text' && !methods.watch('value')) ||
            (methods.watch('type') === 'image' && !methods.watch('file'))
          }
        >
          {'Créer'}
        </button>
      </form>
    </PureModal>
  );
}

export default forwardRef(CreateRessource);
